import type { LocaleConfig } from "./config";

export interface CreateLocalizeHrefOptions {
  config: LocaleConfig;
  /** Same-site origins whose absolute URLs should be treated as internal paths. */
  sameSiteOrigins?: string[];
  /** Whether this app serves the given (unprefixed) route under its `[locale]` segment. */
  isLocalizableRoute: (route: string) => boolean;
  /** Whether the given route's translations have shipped in that one locale. */
  isLaunchedLocalizedRoute: (route: string, locale: string) => boolean;
  /**
   * Paths that must never carry a prefix even if the route tables list them —
   * a separate app behind the CDN serves them, so no locale middleware runs
   * and a prefixed URL 404s rather than redirecting.
   */
  isExcludedPath?: (path: string) => boolean;
}

/**
 * Builds a `localizeHref(href, prefix)` function scoped to the app's own
 * config, same-site origins, and route tables.
 *
 * Leaves untouched: the default locale, external URLs, protocol-relative
 * URLs, anchors, `mailto:`/`tel:`, any non-absolute path, paths this app does
 * not serve localised, and routes whose translations have not yet launched
 * (to prevent redirect loops back to the default locale in production).
 *
 * Also handles absolute same-site URLs (https://www.example.com/pricing) by
 * stripping the origin and applying the same path-based logic.
 *
 *   localizeHref("/view-demos", "ja")              -> "/ja/view-demos"  (if launched)
 *   localizeHref("/view-demos", "en")              -> "/view-demos"
 *   localizeHref("/about", "ja")                   -> "/about"          (not an app route)
 *   localizeHref("https://www.example.com/pricing", "ja") -> "https://www.example.com/ja/pricing" (if launched)
 *   localizeHref("https://x.com", "ja")            -> "https://x.com"
 */
export function createLocalizeHref(
  options: CreateLocalizeHrefOptions,
): (href: string, prefix: string) => string {
  const {
    config,
    sameSiteOrigins = [],
    isLocalizableRoute,
    isLaunchedLocalizedRoute,
    isExcludedPath,
  } = options;

  function localizeHref(href: string, prefix: string): string {
    if (prefix === config.DEFAULT_PREFIX) return href;

    // Absolute same-site URLs: strip origin, localize the path, re-attach origin.
    // Match the origin only when what follows is a path/query/hash boundary (or
    // nothing) so "https://example.com.evil.com" is never treated as same-site.
    for (const origin of sameSiteOrigins) {
      if (
        href === origin ||
        href.startsWith(`${origin}/`) ||
        href.startsWith(`${origin}?`) ||
        href.startsWith(`${origin}#`)
      ) {
        // A root URL carrying only a query/hash (…example.com?x / …#y) has no
        // "/" before it — treat the remainder as the root path so it localises
        // instead of falling through as "not same-site".
        let rest = href.slice(origin.length);
        if (rest === "" || rest.startsWith("?") || rest.startsWith("#")) {
          rest = `/${rest}`;
        }
        const localized = localizeHref(rest, prefix);
        return localized === rest ? href : `${origin}${localized}`;
      }
    }

    if (!href.startsWith("/")) return href;
    if (href.startsWith("//")) return href;

    const queryOrHash = href.search(/[?#]/);
    const path = queryOrHash === -1 ? href : href.slice(0, queryOrHash);
    if (isExcludedPath?.(path)) return href;
    if (!isLocalizableRoute(path)) return href;
    // Only emit a prefixed URL when the route can actually serve this locale,
    // otherwise the middleware redirects the user straight back to the default
    // locale. The pseudo locale never "launches" but is reachable on preview,
    // so QA has to be able to follow links without falling out of /ps.
    if (prefix !== config.PSEUDO_PREFIX) {
      const locale = config.PREFIX_TO_LOCALE[prefix];
      if (!locale || !isLaunchedLocalizedRoute(path, locale)) return href;
    }

    if (path === "/") {
      const suffix = queryOrHash === -1 ? "" : href.slice(queryOrHash);
      return `/${prefix}${suffix}`;
    }
    return `/${prefix}${href}`;
  }

  return localizeHref;
}

export interface CreateLocalizeCrossSiteHrefOptions {
  config: LocaleConfig;
  /**
   * Origins owned by a sibling marketing site that serves the same locale
   * prefixes (fin.ai ↔ intercom.com). Distinct from `sameSiteOrigins`, which
   * are this app's own origins.
   */
  crossSiteOrigins: string[];
  /**
   * Paths on those origins that the sibling's Next.js app does not serve, so
   * its locale middleware never sees them and a prefix 404s rather than
   * redirecting. `/signup` is the live case: a separate React app behind the
   * CDN, and its URLs are matched by exact pathname by the utm_referrer
   * forwarder. Receives the pathname, without query or hash.
   */
  isExcludedPath?: (path: string) => boolean;
}

// Mirrors route.ts's FILE_EXT_RE: prefixing one 404s instead of redirecting.
const CROSS_SITE_FILE_EXT_RE = /\.[^/]+$/;

/**
 * Builds a `localizeCrossSiteHref(href, prefix)` function for links that point
 * at a *different* Intercom marketing site. `localizeHref` deliberately
 * declines these — the target is cross-domain, so this app's route tables say
 * nothing about it — which leaves a localised page linking out to English.
 *
 * This keeps the reader in their locale by carrying our prefix across. The
 * receiving site owns its own `LOCALIZABLE`/`LAUNCHED` gates, so a prefix it
 * has not launched yet 307s to the canonical URL: correct destination, one
 * extra redirect. That is the accepted trade for not encoding another app's
 * route table here.
 *
 * That fallback only holds for paths the sibling's locale middleware actually
 * runs on. `isExcludedPath` covers the ones it does not — see that option.
 *
 *   localizeCrossSiteHref("https://www.intercom.com/pricing", "ja")
 *     -> "https://www.intercom.com/ja/pricing"
 *   localizeCrossSiteHref("https://www.intercom.com/pricing", "en")  -> unchanged
 *   localizeCrossSiteHref("https://example.com/pricing", "ja")       -> unchanged
 */
export function createLocalizeCrossSiteHref(
  options: CreateLocalizeCrossSiteHrefOptions,
): (href: string, prefix: string) => string {
  const { config, crossSiteOrigins, isExcludedPath } = options;

  return function localizeCrossSiteHref(href: string, prefix: string): string {
    if (prefix === config.DEFAULT_PREFIX) return href;

    for (const origin of crossSiteOrigins) {
      if (
        href !== origin &&
        !href.startsWith(`${origin}/`) &&
        !href.startsWith(`${origin}?`) &&
        !href.startsWith(`${origin}#`)
      ) {
        continue;
      }

      let rest = href.slice(origin.length);
      if (rest === "" || rest.startsWith("?") || rest.startsWith("#")) {
        rest = `/${rest}`;
      }

      const queryOrHash = rest.search(/[?#]/);
      const path = queryOrHash === -1 ? rest : rest.slice(0, queryOrHash);
      const suffix = queryOrHash === -1 ? "" : rest.slice(queryOrHash);

      if (CROSS_SITE_FILE_EXT_RE.test(path)) return href;
      if (isExcludedPath?.(path)) return href;

      const [, firstSegment = ""] = path.split("/");
      if (
        firstSegment === config.DEFAULT_PREFIX ||
        config.KNOWN_PREFIXES.includes(firstSegment)
      ) {
        return href;
      }

      const localizedPath = path === "/" ? `/${prefix}` : `/${prefix}${path}`;
      return `${origin}${localizedPath}${suffix}`;
    }

    return href;
  };
}

export interface CreateLocalizeSignupHrefOptions {
  config: LocaleConfig;
  /**
   * Pathnames whose flow reads the `locale` query param, matched on any origin
   * and as a path prefix — "/signup" covers "/signup/email". Include the
   * app's own handoff routes that redirect to one, since Next forwards their
   * query to the destination.
   */
  signupPaths: readonly string[];
}

/** Named by every signup flow; also what signup's `resolveSignupLocale` reads. */
const SIGNUP_LOCALE_PARAM = "locale";

function pathnameOf(href: string): string | null {
  if (/^https?:\/\//.test(href)) {
    try {
      return new URL(href).pathname;
    } catch {
      return null;
    }
  }
  if (href.startsWith("/") && !href.startsWith("//")) {
    return href.split(/[?#]/)[0];
  }
  return null;
}

/**
 * Builds a `localizeSignupHref(href, prefix)` that carries the reader's locale
 * into a signup flow as a `locale` query param.
 *
 * Neither href localizer can prefix these URLs: the signup flows are separate
 * apps behind the CDN, so the marketing site's locale middleware never runs on
 * them and a prefix 404s instead of redirecting. They do read `?locale=`, which
 * is the only way a localised page can hand its locale over. Without this, every
 * trial CTA on a localised page drops the reader into English signup.
 *
 * Emits the BCP-47 locale rather than the URL prefix. Signup resolves an
 * unsupported tag by its language subtag, so a regional locale we serve and it
 * does not (es-419 -> es-MX) still lands in the right language. The pseudo
 * locale is declined outright: it is not a BCP-47 tag and signup has no pseudo
 * catalog to resolve it against.
 *
 *   localizeSignupHref("https://www.intercom.com/signup?cta_domain=fin", "de-de")
 *     -> "https://www.intercom.com/signup?cta_domain=fin&locale=de-DE"
 */
export function createLocalizeSignupHref(
  options: CreateLocalizeSignupHrefOptions,
): (href: string, prefix: string) => string {
  const { config, signupPaths } = options;

  return function localizeSignupHref(href: string, prefix: string): string {
    if (prefix === config.DEFAULT_PREFIX) return href;
    if (prefix === config.PSEUDO_PREFIX) return href;

    const locale = config.PREFIX_TO_LOCALE[prefix];
    if (!locale) return href;

    const path = pathnameOf(href);
    if (!path) return href;
    if (
      !signupPaths.some(
        (signupPath) =>
          path === signupPath || path.startsWith(`${signupPath}/`),
      )
    ) {
      return href;
    }

    const hashAt = href.indexOf("#");
    const hash = hashAt === -1 ? "" : href.slice(hashAt);
    const base = hashAt === -1 ? href : href.slice(0, hashAt);

    const queryAt = base.indexOf("?");
    const query = queryAt === -1 ? "" : base.slice(queryAt + 1);
    if (new URLSearchParams(query).has(SIGNUP_LOCALE_PARAM)) return href;

    const separator = queryAt === -1 ? "?" : "&";
    return `${base}${separator}${SIGNUP_LOCALE_PARAM}=${locale}${hash}`;
  };
}

export interface CreateLocalizeImageSrcOptions {
  config: LocaleConfig;
  /** The directory segment that carries locale-specific assets, e.g. "img" for "/img/en/…". Defaults to "img". */
  imageDirSegment?: string;
}

/**
 * Builds a `localizeImageSrc(src, prefix)` function that swaps the
 * `/{imageDirSegment}/{DEFAULT_PREFIX}/` segment of a localisable image path
 * for the active locale's directory. Only paths that contain that segment are
 * rewritten — images without it carry no text and need no localisation.
 * No-ops for the default locale.
 *
 *   localizeImageSrc("/marketing-site/img/en/x.webp", "pt")
 *     -> "/marketing-site/img/pt/x.webp"
 *
 * Apply only where a localised asset is known to exist; other sections have
 * no translated images.
 */
export function createLocalizeImageSrc(
  options: CreateLocalizeImageSrcOptions,
): (src: string, prefix: string) => string {
  const { config, imageDirSegment = "img" } = options;
  const sourceSegment = `/${imageDirSegment}/${config.DEFAULT_PREFIX}/`;

  return function localizeImageSrc(src: string, prefix: string): string {
    if (prefix === config.DEFAULT_PREFIX) return src;
    return src.replace(sourceSegment, `/${imageDirSegment}/${prefix}/`);
  };
}

export interface CreateLocalizeSanityImageOptions {
  config: LocaleConfig;
  /**
   * Sanity asset URL per stable image key and locale prefix. Starts empty
   * and fills in as translated assets are uploaded — same shape as
   * `getVideoCaptions`/`LOCALE_CAPTIONS`, except every image always has a
   * `defaultSrc` to fall back to.
   */
  lookup: Record<string, Partial<Record<string, string>>>;
}

/**
 * Builds a `localizeSanityImage(imageKey, defaultSrc, prefix)` function that
 * resolves a translated Sanity asset URL from `lookup`, falling back to
 * `defaultSrc` when no variant exists yet for that locale. No-ops for the
 * default locale.
 *
 *   localizeSanityImage("early-stage-hero", "https://cdn.sanity.io/.../en.webp", "ja")
 *     -> the uploaded ja URL, or the English default if none exists yet
 */
export function createLocalizeSanityImage(
  options: CreateLocalizeSanityImageOptions,
): (imageKey: string, defaultSrc: string, prefix: string) => string {
  const { config, lookup } = options;

  return function localizeSanityImage(
    imageKey: string,
    defaultSrc: string,
    prefix: string,
  ): string {
    if (prefix === config.DEFAULT_PREFIX) return defaultSrc;
    return lookup[imageKey]?.[prefix] ?? defaultSrc;
  };
}

/**
 * A still that stands in for a Rive animation. Structurally a `RivePlaceholder`
 * (`@marketing-sites/ui/rive`) minus `priority`; spelled out here so this
 * package keeps no dependency on the UI package.
 */
export interface RiveStill {
  src: string;
  alt: string;
}

export interface CreateLocalizeRiveStillOptions {
  config: LocaleConfig;
  /**
   * Still-image URL per stable Rive key and locale prefix. Starts empty and
   * fills in as translated stills are uploaded — same file shape as
   * `createLocalizeSanityImage`'s lookup, but deliberately a separate file:
   * see the miss semantics on `createLocalizeRiveStill`.
   */
  lookup: Record<string, Partial<Record<string, string>>>;
}

/**
 * Builds a `localizeRiveStill(riveKey, alt, prefix)` function that resolves the
 * translated still standing in for a Rive animation whose artboard has English
 * text baked in, and returns `undefined` when there isn't one.
 *
 * `undefined` is the whole point: a miss means "keep playing the animation",
 * which is why this can't share `createLocalizeSanityImage`'s lookup — there a
 * miss falls back to the English image, so an unrelated key collision would
 * silently replace an animation with a still.
 *
 *   localizeRiveStill("home-hero", "Fin resolving a ticket", "ja")
 *     -> { src: <uploaded ja still>, alt: "Fin resolving a ticket" }
 *   localizeRiveStill("home-hero", "…", "en")  -> undefined
 */
export function createLocalizeRiveStill(
  options: CreateLocalizeRiveStillOptions,
): (riveKey: string, alt: string, prefix: string) => RiveStill | undefined {
  const { config, lookup } = options;

  return function localizeRiveStill(
    riveKey: string,
    alt: string,
    prefix: string,
  ): RiveStill | undefined {
    if (prefix === config.DEFAULT_PREFIX) return undefined;
    const src = lookup[riveKey]?.[prefix];
    return src === undefined ? undefined : { src, alt };
  };
}
